import type { ExternalPriceFuelType } from "./externalPriceTypes";
import {
    type AemFuelTypeSpecNames,
    type VehicleType,
    aemFuelTypeToDfFuelTypeMap,
} from "../../shared-logic/types/UscCommonTypes";
import { DfFuelTypeId, DfVehicleOperationModeCode } from "../../shared-logic/utils/dfConstants";
import { fuelTypeMap } from "./externalPriceUtils";
import { formatPriceIntl } from "../../../common-deprecated/priceUtils";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import { type EcoMeasureType, getUscEcoMeasureValues } from "../../shared-logic/utils/uscEcoUtils";

export type EcoCostsReducerType = {
    initialized: boolean;
    failed: boolean;
    data: EcoCostsType | null;

    // For debugging purposes
    payload: EcoCostsPayloadType | null;
    errors: string[];
};

export type EcoCostsType = {
    pricesForYear: string; // year for which fuel/energy prices are used
    disclaimerFrom: string; // disclaimer “from” validity value
    disclaimerTo: string; // disclaimer “to” validity value
    yearlyCostEuros: number; // yearly fuel and/or energy cost
    fuelUnitPriceEuros: number; // fuel unit (litre or kg) price in euros
    electricityKwhPriceCents: number; // electricity kWh price in cents
    co2CostMedium: number; // average CO2 emission cost in euros
    co2CostLow: number; // low CO2 emission cost in euros
    co2TonPriceHigh: number; // high CO2 emission cost in euros
    co2TonPriceMedium: number; // average CO2 ton price in euros
    co2TonPriceLow: number; // low CO2 ton price in euros
    co2CostHigh: number; // high CO2 ton price in euros
    taxYearly: number; // total yearly tax amount in euros
    errors: string[]; // list of errors
};

type WltpTechSpecType = {
    TestPhaseName: string;
    Category: string;
    Type: string;
    Mode: string;
    Value: string;
};

/**
 * The ecoCosts payload is closely related to the
 * ExternalPrice payload, but it's not feasible to type and use it as
 * a DeepPartial of ExternalPricePayloadType. Therefore, it's defined
 * separately.
 */
export type EcoCostsPayloadType = {
    Product: {
        FuelType: ExternalPriceFuelType;
        Hybrid: boolean;
        PlugIn: boolean;
    };
    TechnicalSpecifications: {
        WLTP: WltpTechSpecType[];
        A2P: { MasterID: string; Value: string }[];
    };
};

export type EcoCostsLabelsType = {
    annualEnergyCostTitle: string;
    annualEnergyCost: string;
    co2CostTitle: string;
    mediumCo2Price: string;
    lowCo2Price: string;
    highCo2Price: string;
    vehicleTaxTitle: string;
};

export type EcolabelTmefConfigType = Partial<Record<Exclude<AemFuelTypeSpecNames, "Other">, EcoCostsLabelsType>>;

/**
 * Possible label keys to use in the TMEF section.
 */
enum EcoCostsLabelKey {
    PricesForYear = "pricesForYear",
    FuelUnitPriceEuros = "fuelUnitPriceEuros",
    ElectricityKwhPriceCents = "electricityKwhPriceCents",
    DisclaimerFrom = "disclaimerFrom",
    DisclaimerTo = "disclaimerTo",
    co2TonPriceHigh = "co2TonPriceHigh",
    co2TonPriceMedium = "co2TonPriceMedium",
    co2TonPriceLow = "co2TonPriceLow",
}

/**
 * Maps Data Foundation consumption modes to TMEF consumption modes.
 */
const wltpModeMap: Partial<Record<DfVehicleOperationModeCode, string>> = {
    [DfVehicleOperationModeCode.NotApplicable]: "Normal",
    [DfVehicleOperationModeCode.ChargeSustaining]: "CS",
    [DfVehicleOperationModeCode.UfWeighted]: "UF Weighted",
};

const getWltpSpec = (
    category: "EC" | "FC" | "CO2",
    measureInfo: EcoMeasureType | null,
    // FIXME: Temporarily allowing to overwrite the mode to handle the following cases:
    // BEV: Energy Consumption has TMEF mode "EC" -> I expect "Normal" here
    overwriteMode?: string,
): WltpTechSpecType | null => {
    if (!measureInfo) return null;

    const { value, mode } = measureInfo;

    if (!value || !mode) return null;

    return {
        TestPhaseName: "Combined",
        Category: category,
        Type: "vehicleIndividual",
        Mode: overwriteMode || wltpModeMap[mode.code] || "",
        Value: String(value),
    };
};

/**
 * Creates an EcoCosts payload from a vehicle, to be sent to
 * the TMEF eco_costs endpoint.
 */
export const getEcoCostsPayload = (vehicle: VehicleType | null): EcoCostsPayloadType | null => {
    if (!vehicle) return null;

    const fuelType = vehicle.product.engine.marketingFuelType.code;

    if (fuelType !== DfFuelTypeId.Other) {
        const ecoMeasureValues = getUscEcoMeasureValues(
            vehicle.product.eco,
            vehicle.product.engine.plugin || vehicle.product.engine.hybrid,
        );

        const overwriteMode = fuelType === DfFuelTypeId.Electric ? "EC" : undefined;

        const co2WltpSpec = getWltpSpec("CO2", ecoMeasureValues.co2WltpCombined);
        const fcWltpSpec = getWltpSpec("FC", ecoMeasureValues.fcWltpCombined);
        const ecWltpSpec = getWltpSpec("EC", ecoMeasureValues.ecCombined, overwriteMode);

        const wltpSpecs = [co2WltpSpec, fcWltpSpec, ecWltpSpec].filter((spec) => spec !== null) as WltpTechSpecType[];

        return {
            Product: {
                FuelType: fuelTypeMap[vehicle.product.engine.fuelType.code] || "",
                Hybrid: vehicle.product.engine.hybrid,
                PlugIn: vehicle.product.engine.plugin,
            },
            TechnicalSpecifications: {
                WLTP: wltpSpecs,
                A2P: [
                    {
                        MasterID: "078601A5-0688-4FEF-8AA0-520BEEC4DC44",
                        Value: String(vehicle.product.engine.displacement?.value || 0),
                    },
                ],
            },
        };
    }

    return null;
};

/**
 * The NMSC configures the labels to shown in the TMEF section,
 * containing placeholders for the actual values.
 * This function replaces the placeholders with the actual values.
 */
export const formatEcoCostsLabels = (
    commonSettings: CommonSettingsType,
    ecoCosts: EcoCostsType | null,
    labels: EcoCostsLabelsType | undefined,
): Record<keyof EcoCostsLabelsType, string> | null => {
    const intlOptions = (value: number): Intl.NumberFormatOptions => ({
        minimumFractionDigits: value,
        maximumFractionDigits: value,
    });

    if (!ecoCosts || !labels) return null;

    const ecoCostsReplacements: Record<EcoCostsLabelKey, string> = {
        [EcoCostsLabelKey.PricesForYear]: ecoCosts.pricesForYear,
        [EcoCostsLabelKey.FuelUnitPriceEuros]: formatPriceIntl(
            commonSettings,
            ecoCosts.fuelUnitPriceEuros,
            intlOptions(3),
        ).primaryPrice,
        [EcoCostsLabelKey.ElectricityKwhPriceCents]: formatPriceIntl(
            commonSettings,
            ecoCosts.electricityKwhPriceCents,
            // Edge case: electricity price is shown as "ct/kWh" instead of "€/kWh"
            { currency: undefined, style: "decimal", minimumFractionDigits: 3, maximumFractionDigits: 3 },
        ).primaryPrice,
        [EcoCostsLabelKey.DisclaimerFrom]: ecoCosts.disclaimerFrom,
        [EcoCostsLabelKey.DisclaimerTo]: ecoCosts.disclaimerTo,
        [EcoCostsLabelKey.co2TonPriceHigh]: formatPriceIntl(commonSettings, ecoCosts.co2TonPriceHigh, intlOptions(2))
            .primaryPrice,
        [EcoCostsLabelKey.co2TonPriceMedium]: formatPriceIntl(
            commonSettings,
            ecoCosts.co2TonPriceMedium,
            intlOptions(2),
        ).primaryPrice,
        [EcoCostsLabelKey.co2TonPriceLow]: formatPriceIntl(commonSettings, ecoCosts.co2TonPriceLow, intlOptions(2))
            .primaryPrice,
    };

    // Empty object to store the formatted labels
    const formattedLabels: Record<keyof EcoCostsLabelsType, string> = {} as Record<keyof EcoCostsLabelsType, string>;

    // For each label, replace the placeholders with the actual values
    // This is similar to replaceKeysInFormat, but simplified and uses a
    // different format for placeholders ({placeholder} instead of [placeholder]{value}[/placeholder])
    Object.keys(labels).forEach((key) => {
        const label = labels[key as keyof EcoCostsLabelsType];

        // Regex matches any placeholder in the format {placeholder}
        const formattedLabel = label.replace(/\{(\w+)\}/g, (_, placeholder) => {
            // If we have a replacement for the placeholder, return it,
            // otherwise return an empty string (to avoid showing the placeholder)
            return ecoCostsReplacements[placeholder as EcoCostsLabelKey] || "";
        });

        formattedLabels[key as keyof EcoCostsLabelsType] = formattedLabel;
    });

    return formattedLabels;
};

/**
 * Returns the eco costs labels for the given fuel type.
 * Returns null if the labels are not available.
 */
export const getEcoCostsLabels = (
    ecoCostsLabels: EcolabelTmefConfigType,
    fuelType: DfFuelTypeId,
): EcoCostsLabelsType | null => {
    const aemFuelTypeName = aemFuelTypeToDfFuelTypeMap[fuelType];

    return ecoCostsLabels[aemFuelTypeName] || null;
};

/**
 * Used to format eco cost price values for the TMEF section.
 */
export const formatEcoCostPrice = (
    commonSettings: CommonSettingsType,
    price: number,
    placeholderLabel?: string,
): string => {
    const formattedPrice = formatPriceIntl(commonSettings, price, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).primaryPrice;

    if (placeholderLabel) {
        return placeholderLabel.replace("{value}", formattedPrice);
    }

    return formattedPrice;
};

type Stringified<T> = {
    [P in keyof T]: string;
};

/**
 * Returns the relevant formatted eco costs data for the TMEF section.
 */
export const formatEcoCostsData = (
    commonSettings: CommonSettingsType,
    ecoCosts: EcoCostsType | null,
    yearPlaceholderLabel?: string,
): Partial<Stringified<EcoCostsType>> => {
    if (!ecoCosts) return {};

    return {
        yearlyCostEuros: formatEcoCostPrice(commonSettings, ecoCosts.yearlyCostEuros, yearPlaceholderLabel),
        co2CostMedium: formatEcoCostPrice(commonSettings, ecoCosts.co2CostMedium),
        co2CostLow: formatEcoCostPrice(commonSettings, ecoCosts.co2CostLow),
        co2CostHigh: formatEcoCostPrice(commonSettings, ecoCosts.co2CostHigh),
        taxYearly: formatEcoCostPrice(commonSettings, ecoCosts.taxYearly, yearPlaceholderLabel),
    };
};
